import { useEffect, useState, type FormEvent } from 'react';
import type { ChildProfile } from '../../shared/types/profiles';
import { SKILL_BANDS, type SkillBand } from '../../shared/types/ids';
import { GUEST_PLAYER, useAppServices } from '../shell/services-context';

/**
 * Profiles are local, account-free and hold no personal data beyond a nickname the family
 * chooses (architecture §19). A guest can play immediately without creating anything.
 */

function newChildProfile(nickname: string, band: SkillBand): ChildProfile {
  const now = new Date().toISOString();
  return {
    id: `child-${crypto.randomUUID()}`,
    nickname,
    band,
    accessibility: {
      reducedMotion: false,
      jumpSubstitute: false,
      narrationEnabled: true,
      narrationVolume: 1,
      highContrast: false,
    },
    activityPreference: 'MEDIUM',
    subjectEstimates: {},
    thriveAchievements: [],
    gameProgress: {},
    createdAt: now,
    updatedAt: now,
  };
}

export function ProfilesPage() {
  const { repositories, activePlayer, setActivePlayer } = useAppServices();
  const [children, setChildren] = useState<ChildProfile[]>([]);
  const [nickname, setNickname] = useState('');
  const [band, setBand] = useState<SkillBand>('explorer');
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const list = await repositories.profiles.listChildren();
      if (!cancelled) setChildren(list);
    })();
    return () => {
      cancelled = true;
    };
  }, [repositories]);

  const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const trimmed = nickname.trim();
    if (trimmed.length === 0) {
      setStatus('Please type a name first.');
      return;
    }

    const profile = newChildProfile(trimmed, band);
    void (async () => {
      await repositories.profiles.saveChild(profile);
      setChildren(await repositories.profiles.listChildren());
      setNickname('');
      setStatus(`${profile.nickname} is ready to play.`);
      setActivePlayer({
        playerId: profile.id,
        profileId: profile.id,
        label: profile.nickname,
        band: profile.band,
        isGuest: false,
        // Which generation of this id was chosen (P6R17-1). Captured here, at the choice itself.
        profileCreatedAt: profile.createdAt,
      });
    })();
  };

  return (
    <section>
      <h1>Players</h1>
      <p className="lede">Everyone who plays on this device. Nothing is sent anywhere.</p>

      <form className="panel" onSubmit={handleSubmit} aria-label="Add a player">
        <div className="field">
          <label htmlFor="nickname">Name or nickname</label>
          <input
            id="nickname"
            name="nickname"
            value={nickname}
            autoComplete="off"
            onChange={(event) => {
              setNickname(event.target.value);
            }}
          />
        </div>

        <div className="field">
          <label htmlFor="band">Starting level</label>
          <select
            id="band"
            name="band"
            value={band}
            onChange={(event) => {
              setBand(event.target.value as SkillBand);
            }}
          >
            {SKILL_BANDS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="button">
          Add player
        </button>
      </form>

      {status === null ? null : (
        <p className="notice" role="status">
          {status}
        </p>
      )}

      <h2>On this device</h2>
      {children.length === 0 ? (
        <p className="notice">No players yet — add one above, or play as a guest.</p>
      ) : (
        <ul className="player-list" data-testid="player-list">
          {children.map((child) => (
            <li key={child.id}>
              <strong>{child.nickname}</strong>
              <span className="badge">{child.band}</span>
              <span
                className={
                  activePlayer?.profileId === child.id ? 'badge badge--active' : 'visually-hidden'
                }
              >
                {activePlayer?.profileId === child.id ? 'Playing' : ''}
              </span>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => {
                  setActivePlayer({
                    playerId: child.id,
                    profileId: child.id,
                    label: child.nickname,
                    band: child.band,
                    isGuest: false,
                    // The generation this grown-up is pointing at, not whoever holds the id later.
                    profileCreatedAt: child.createdAt,
                  });
                }}
              >
                Choose
              </button>
            </li>
          ))}
        </ul>
      )}

      <h2>Just visiting?</h2>
      <p className="lede">A guest can play straight away. Nothing about them is saved.</p>
      <button
        type="button"
        className="button button--quiet"
        onClick={() => {
          setActivePlayer(GUEST_PLAYER);
        }}
      >
        Play as guest
      </button>
    </section>
  );
}
